import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box, Typography, Chip, ToggleButton, ToggleButtonGroup, Slider,
  TextField, IconButton, Button, Collapse, Switch, Tooltip,
} from '@mui/material';
import TuneIcon from '@mui/icons-material/Tune';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddIcon from '@mui/icons-material/Add';
import TableRowsIcon from '@mui/icons-material/TableRows';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import TrainIcon from '@mui/icons-material/Train';
import SchoolIcon from '@mui/icons-material/School';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import { PsfColors } from '../theme';
import {
  AdjustmentState, Level, seedCurve, CurvePoint, AgeBandAdjust, ExtraAdjust, FreeholdMethod, amenityPremiumAmount,
} from '../lib/adjustments';
import { submitOtherMethod, ProjectSummary } from '../services/api';
import DepreciationCurve from './DepreciationCurve';
import AmenityPremiumCard, { AmenityProjectPoint } from './AmenityPremiumCard';

interface AdjustmentPanelProps {
  state: AdjustmentState;
  onChange: (next: AdjustmentState) => void;
  c: PsfColors;
  /** Projects currently in the table — drives the Level 4 distance charts and scoreboard. */
  amenityProjects?: ProjectSummary[];
}

const toPoint = (distance: unknown, basePsf: unknown): AmenityProjectPoint => {
  const d = distance == null || distance === '' ? NaN : Number(distance);
  const psf = basePsf == null || basePsf === '' ? NaN : Number(basePsf);
  return { distance: Number.isFinite(d) ? d : null, basePsf: Number.isFinite(psf) ? psf : null };
};

function InfoBox({ children, c, accent }: { children: React.ReactNode; c: PsfColors; accent: string }) {
  return (
    <Box sx={{ p: 1, bgcolor: `${accent}11`, borderRadius: 1, mt: 1 }}>
      <Typography variant="caption" sx={{ color: c.textMuted, fontSize: '0.63rem', lineHeight: 1.55, display: 'block' }}>
        <InfoOutlinedIcon sx={{ fontSize: 10, mr: 0.5, verticalAlign: 'middle' }} />
        {children}
      </Typography>
    </Box>
  );
}

function ParamSlider({
  label, value, onChange, min, max, step = 1, unit, c,
}: {
  label: string; value: number; onChange: (v: number) => void;
  min: number; max: number; step?: number; unit: string; c: PsfColors;
}) {
  return (
    <Box sx={{ mb: 1 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
        <Typography variant="caption" sx={{ color: c.textMuted, fontSize: '0.68rem' }}>{label}</Typography>
        <Typography variant="caption" sx={{ color: c.orange, fontWeight: 700, fontSize: '0.68rem' }}>
          {value}{unit}
        </Typography>
      </Box>
      <Slider
        value={value}
        onChange={(_, v) => onChange(v as number)}
        min={min} max={max} step={step}
        size="small"
        sx={{
          color: c.orange, py: 0.5,
          '& .MuiSlider-thumb': { width: 13, height: 13 },
          '& .MuiSlider-track': { height: 3 },
          '& .MuiSlider-rail': { height: 3, opacity: 0.3 },
        }}
      />
    </Box>
  );
}

function DraftNumberField({ value, onChange, sx }: { value: number; onChange: (value: number) => void; sx: any }) {
  const [draft, setDraft] = useState(String(value));
  useEffect(() => setDraft(String(value)), [value]);

  return (
    <TextField
      size="small"
      type="number"
      value={draft}
      onChange={(event) => {
        const next = event.target.value;
        setDraft(next);
        if (next !== '' && next !== '-') onChange(Number(next));
      }}
      onBlur={() => {
        if (draft === '' || draft === '-') {
          setDraft('0');
          onChange(0);
        }
      }}
      onFocus={(event) => {
        if (draft === '0') event.currentTarget.select();
      }}
      sx={sx}
    />
  );
}

export default function AdjustmentPanel({ state, onChange, c, amenityProjects = [] }: AdjustmentPanelProps) {
  const C = c;
  const amenityPoints = useMemo(() => ({
    mrt: amenityProjects.map((project) => toPoint(project.nearestMrtDistance, project.avgPsf)),
    priSch: amenityProjects.map((project) => toPoint(project.nearestPriSchDistance, project.avgPsf)),
  }), [amenityProjects]);
  // Combined Level 4 result across both amenities, for the header trophy.
  const amenityTotals = useMemo(() => {
    let lifted = 0;
    let total = 0;
    amenityPoints.mrt.forEach((mrt, index) => {
      if (mrt.basePsf == null) return;
      const sch = amenityPoints.priSch[index];
      const add = amenityPremiumAmount(state.mrtPremium, mrt.distance, mrt.basePsf)
        + amenityPremiumAmount(state.priSchPremium, sch.distance, mrt.basePsf);
      if (add !== 0) { lifted += 1; total += add; }
    });
    return { lifted, avg: lifted ? Math.round(total / lifted) : 0 };
  }, [amenityPoints, state.mrtPremium, state.priSchPremium]);
  const [curveMode, setCurveMode] = useState<'graph' | 'table'>('graph');
  const [level2Open, setLevel2Open] = useState(false);
  const [level3Open, setLevel3Open] = useState(true);
  const [leaseholdTab, setLeaseholdTab] = useState<'linear' | 'other'>('linear');
  const [freeholdTab, setFreeholdTab] = useState<FreeholdMethod | 'other'>(() => state.freeholdMethod);
  const [otherMethodForm, setOtherMethodForm] = useState({ remarks: '' });
  const [otherMethodSubmitting, setOtherMethodSubmitting] = useState(false);
  const [otherMethodError, setOtherMethodError] = useState<string | null>(null);
  const [otherMethodSubmitted, setOtherMethodSubmitted] = useState(false);
  const levelRefs = useRef<Partial<Record<Level, HTMLDivElement | null>>>({});

  /** Change a methodology parameter — reseeds the curve (discards manual edits). */
  const updateAndReseed = (partial: Partial<AdjustmentState>) => {
    const next = { ...state, ...partial, curveEdited: false };
    next.curve = seedCurve(next);
    onChange(next);
  };

  const setCurve = (curve: CurvePoint[]) => onChange({ ...state, curve, curveEdited: true });

  const updateBand = (id: string, partial: Partial<AgeBandAdjust>) => {
    onChange({ ...state, ageBands: state.ageBands.map((b) => (b.id === id ? { ...b, ...partial } : b)) });
  };

  const updateExtra = (id: string, partial: Partial<ExtraAdjust>) => {
    onChange({ ...state, extras: state.extras.map((x) => (x.id === id ? { ...x, ...partial } : x)) });
  };

  const isLeasehold = state.tenureChoice === 'leasehold';

  useEffect(() => {
    // The selected level is always expanded. Lower levels collapse when the
    // user advances so the active controls are immediately visible.
    setLevel2Open(state.level === 2);
    setLevel3Open(state.level === 3);
  }, [state.level]);

  useEffect(() => {
    if (!isLeasehold || state.level < 2) setLeaseholdTab('linear');
  }, [isLeasehold, state.level]);

  useEffect(() => {
    if (!isLeasehold) setFreeholdTab(state.freeholdMethod);
  }, [isLeasehold, state.freeholdMethod]);

  // Bala's Table is no longer exposed in the UI. Normalize any legacy
  // in-memory state once so the visible calculation and method label remain
  // consistent with the new Leasehold options.
  useEffect(() => {
    if (isLeasehold && state.leaseholdMethod === 'bala') {
      const next = { ...state, leaseholdMethod: 'linear' as const, curveEdited: false };
      next.curve = seedCurve(next);
      onChange(next);
    }
  }, [isLeasehold, onChange, state]);

  const submitOtherMethodForm = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setOtherMethodError(null);
    setOtherMethodSubmitted(false);
    setOtherMethodSubmitting(true);
    try {
      await submitOtherMethod({
        remarks: otherMethodForm.remarks,
        tenure: isLeasehold ? 'leasehold' : 'freehold',
      });
      setOtherMethodSubmitted(true);
      setOtherMethodForm({ remarks: '' });
    } catch (error: any) {
      setOtherMethodError(error?.message || 'Unable to save your submission. Please try again.');
    } finally {
      setOtherMethodSubmitting(false);
    }
  };

  const methodLabel = isLeasehold
    ? 'Linear'
    : (state.freeholdMethod === 'landBuilding' ? 'Land + Building' : 'Market Discount');
  const showingOtherMethod = isLeasehold ? leaseholdTab === 'other' : freeholdTab === 'other';

  const numFieldSx = {
    '& input': { color: C.text, fontSize: '0.7rem', py: 0.4, px: 0.75 },
    '& .MuiOutlinedInput-notchedOutline': { borderColor: C.border },
  } as const;

  const otherMethodFieldSx = {
    ...numFieldSx,
    '& .MuiOutlinedInput-root': { minHeight: 60 },
    '& .MuiInputBase-input': { color: C.text, fontSize: '1rem', lineHeight: 1.5, py: 1, px: 1.5 },
    '& .MuiOutlinedInput-input:not(.MuiInputBase-inputMultiline)': {
      padding: '16px 12px',
    },
    '& .MuiOutlinedInput-root.MuiInputBase-multiline': {
      padding: 0,
    },
    '& .MuiOutlinedInput-input.MuiInputBase-inputMultiline': {
      // Match the single-line fields while keeping the multiline root free
      // of any additional outer inset.
      padding: '16px 12px',
      margin: 0,
    },
    '& .MuiInputLabel-root': { color: C.textMuted, fontSize: '1rem' },
    '& .MuiInputLabel-root:not(.MuiInputLabel-shrink)': {
      transform: 'translate(14px, 18px) scale(1)',
    },
    // Keep the native floating-label notch, but remove MUI's extra horizontal
    // inset so the notch follows the measured label width.
    '& .MuiOutlinedInput-notchedOutline legend span': { paddingLeft: 0, paddingRight: 0 },
    '& textarea': { color: C.text, fontSize: '1rem', lineHeight: 1.5 },
  } as const;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: 0, height: '100%' }}>
      {/* Header */}
      <Box sx={{ px: 1.5, py: 1.25, borderBottom: `1px solid ${C.border}`, display: 'flex', alignItems: 'center', gap: 1 }}>
        <TuneIcon sx={{ color: C.orange, fontSize: 18 }} />
        <Typography sx={{ fontWeight: 800, color: C.text, fontSize: '0.82rem', flex: 1 }}>
          Adjustments
        </Typography>
        <Chip
          size="small"
          label={isLeasehold ? 'Leasehold' : '999-yr / Freehold'}
          sx={{ bgcolor: `${C.navy}33`, color: C.textMuted, fontSize: '0.62rem', height: 18 }}
        />
      </Box>

      <Box sx={{ flex: 1, overflowY: 'auto', p: 1.5 }}>
        {/* ── Level selector ── */}
        <Box sx={{ display: 'flex', gap: 0.75, mb: 1.5 }}>
          {([1, 2, 3, 4] as Level[]).map((l) => {
            const active = state.level === l;
            const color = l === 1 ? C.navyLight : l === 2 ? C.orange : l === 3 ? C.yellow : C.orange;
            return (
              <Box
                key={l}
                onClick={() => {
                  onChange({ ...state, level: l });
                  requestAnimationFrame(() => levelRefs.current[l]?.scrollIntoView({ behavior: 'smooth', block: 'start' }));
                }}
                sx={{
                  flex: 1, p: 0.75, textAlign: 'center', cursor: 'pointer', borderRadius: 1.5,
                  bgcolor: active ? `${color}22` : C.surface,
                  border: `1.5px solid ${active ? color : C.border}`,
                  transition: 'all 0.15s',
                }}
              >
                <Typography sx={{ fontSize: '0.6rem', fontWeight: 800, color: active ? color : C.textMuted }}>
                  LEVEL {l}
                </Typography>
                <Typography sx={{ fontSize: '0.58rem', color: C.textMuted, lineHeight: 1.2 }}>
                  {l === 1 ? 'Raw' : l === 2 ? 'Tenure' : l === 3 ? 'Physical' : 'Amenities'}
                </Typography>
              </Box>
            );
          })}
        </Box>

        {/* ── Level 1 note ── */}
        {state.level === 1 && (
          <InfoBox c={C} accent={C.navyLight}>
            Raw view — Personalized PSF equals each project&apos;s average transacted PSF. Step up to Level 2 to
            restore tenure depreciation, and Level 3 to add physical &amp; functional wear, so aged resale PSF becomes
            directly comparable with brand-new launch pricing. Level 4 adds optional amenity adjustments.
          </InfoBox>
        )}

        {/* ── Level 2 — tenure depreciation ── */}
        {state.level >= 2 && (
          <Box ref={(node: HTMLDivElement | null) => { levelRefs.current[2] = node; }} sx={{ mb: 1.5, p: 1.25, borderRadius: 1.5, border: `1px solid ${C.orange}55`, bgcolor: `${C.orange}08` }}>
            <Box
              sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: state.level > 2 ? 'pointer' : 'default' }}
              onClick={() => { if (state.level > 2) setLevel2Open((v) => !v); }}
            >
              <Typography sx={{ fontSize: '0.7rem', fontWeight: 800, color: C.orange, mb: 0.75 }}>
                LEVEL 2 · TENURE DEPRECIATION
              </Typography>
              {state.level > 2 && (
                <IconButton size="small" sx={{ color: C.textMuted, p: 0.25 }}>
                  {level2Open ? <ExpandLessIcon sx={{ fontSize: 16 }} /> : <ExpandMoreIcon sx={{ fontSize: 16 }} />}
                </IconButton>
              )}
            </Box>

            <Collapse in={state.level === 2 || level2Open}>
            {/* Methodology toggle */}
            <ToggleButtonGroup
              value={isLeasehold ? (leaseholdTab === 'other' ? 'other' : 'linear') : freeholdTab}
              exclusive
              onChange={(_, v) => {
                if (!v) return;
                if (isLeasehold) {
                  if (v === 'other') {
                    setOtherMethodError(null);
                    setOtherMethodSubmitted(false);
                    setLeaseholdTab('other');
                  } else {
                    setLeaseholdTab('linear');
                    // Returning to Linear is a view change when Linear is
                    // already active; do not reseed or discard curve edits.
                    if (state.leaseholdMethod !== 'linear') {
                      updateAndReseed({ leaseholdMethod: 'linear' });
                    }
                  }
                } else if (v === 'other') {
                  setOtherMethodError(null);
                  setOtherMethodSubmitted(false);
                  setFreeholdTab('other');
                } else {
                  const method = v as FreeholdMethod;
                  setFreeholdTab(method);
                  // Returning to the already active method is only a view
                  // change; preserve any existing curve edits.
                  if (state.freeholdMethod !== method) {
                    updateAndReseed({ freeholdMethod: method });
                  }
                }
              }}
              size="small"
              fullWidth
              sx={{
                mb: 1,
                // Keep the methodology tabs in one row on the desktop/laptop
                // adjustment panel.  The panel is deliberately narrow, so
                // allow the buttons to share the available width rather than
                // wrapping the last tab onto a second row.  On the mobile
                // drawer, wrapping remains preferable to horizontal overflow.
                flexWrap: { xs: 'wrap', md: 'nowrap' },
                '& .MuiToggleButton-root': {
                  flex: { xs: '0 1 auto', md: 1 },
                  minWidth: 0,
                  whiteSpace: 'nowrap',
                  py: 0.4,
                  px: { xs: 1, md: 0.5 },
                  color: C.textMuted,
                  borderColor: C.border,
                  fontSize: { xs: '0.66rem', md: '0.6rem' },
                  textTransform: 'none',
                },
                '& .Mui-selected': { bgcolor: `${C.orange}22 !important`, color: `${C.orange} !important`, borderColor: `${C.orange} !important` },
              }}
            >
              {isLeasehold ? [
                <ToggleButton key="linear" value="linear">Linear</ToggleButton>,
                <ToggleButton key="other" value="other">Other Methods</ToggleButton>,
              ] : [
                <ToggleButton key="landBuilding" value="landBuilding">Land + Building</ToggleButton>,
                <ToggleButton key="marketDiscount" value="marketDiscount">Market Discount</ToggleButton>,
                <ToggleButton key="other" value="other">Other Methods</ToggleButton>,
              ]}
            </ToggleButtonGroup>

            {showingOtherMethod ? (
              <Box component="form" onSubmit={submitOtherMethodForm} sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                <Typography sx={{ color: C.textMuted, fontSize: '0.68rem', lineHeight: 1.55, mb: 2 }}>
                  If you have another model or approach in mind, please share it with us—we’d love to explore and evaluate your ideas.
                </Typography>
                <TextField
                  required
                  label="Remarks *"
                  value={otherMethodForm.remarks}
                  onChange={(event) => setOtherMethodForm((current) => ({ ...current, remarks: event.target.value }))}
                  size="medium"
                  fullWidth
                  multiline
                  minRows={4}
                  InputLabelProps={{ required: false }}
                  sx={otherMethodFieldSx}
                />
                {otherMethodError && (
                  <Typography role="alert" sx={{ color: C.danger, fontSize: '0.66rem' }}>
                    {otherMethodError}
                  </Typography>
                )}
                {otherMethodSubmitted && (
                  <Typography role="status" sx={{ color: C.success, fontSize: '0.66rem' }}>
                    Your feedback has been recorded and we will review it as soon as possible.
                  </Typography>
                )}
                <Button
                  type="submit"
                  variant="outlined"
                  size="small"
                  disabled={otherMethodSubmitting}
                  sx={{ alignSelf: 'flex-start', color: C.orange, borderColor: C.orange, textTransform: 'none', fontSize: '0.68rem' }}
                >
                  {otherMethodSubmitting ? 'Submitting…' : 'Submit suggestion'}
                </Button>
              </Box>
            ) : (
              <>
            {/* Method parameters */}
            {isLeasehold && state.leaseholdMethod === 'linear' && (
              <ParamSlider
                label="Lease term — value reaches $0 at this age"
                value={state.linearTermYears}
                onChange={(v) => updateAndReseed({ linearTermYears: v })}
                min={30} max={103} step={1} unit=" yrs" c={C}
              />
            )}
            {!isLeasehold && state.freeholdMethod === 'landBuilding' && (
              <>
                <ParamSlider
                  label="Land share of value (never depreciates)"
                  value={state.landSharePct}
                  onChange={(v) => updateAndReseed({ landSharePct: v })}
                  min={0} max={95} unit="%" c={C}
                />
                <ParamSlider
                  label="Building economic life"
                  value={state.buildingLifeYears}
                  onChange={(v) => updateAndReseed({ buildingLifeYears: v })}
                  min={30} max={99} unit=" yrs" c={C}
                />
              </>
            )}
            {!isLeasehold && state.freeholdMethod === 'marketDiscount' && (
              <>
                <ParamSlider
                  label="Observed age discount per year"
                  value={state.marketRatePct}
                  onChange={(v) => updateAndReseed({ marketRatePct: v })}
                  min={0} max={2} step={0.1} unit="%/yr" c={C}
                />
                <ParamSlider
                  label="Cap on total discount"
                  value={state.marketCapPct}
                  onChange={(v) => updateAndReseed({ marketCapPct: v })}
                  min={5} max={60} unit="%" c={C}
                />
              </>
            )}

            {/* Curve editor */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 0.5, mb: 0.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Typography sx={{ fontSize: '0.66rem', fontWeight: 700, color: C.text }}>
                  Depreciation curve
                </Typography>
                <Chip
                  size="small"
                  label={state.curveEdited ? 'Custom (edited)' : methodLabel}
                  sx={{
                    height: 16, fontSize: '0.56rem',
                    bgcolor: state.curveEdited ? `${C.yellow}22` : `${C.orange}18`,
                    color: state.curveEdited ? C.yellow : C.orange,
                  }}
                />
              </Box>
              <Box sx={{ display: 'flex', gap: 0.25 }}>
                <Tooltip title="Graph editor">
                  <IconButton size="small" onClick={() => setCurveMode('graph')}
                    sx={{ color: curveMode === 'graph' ? C.orange : C.textMuted, p: 0.4 }}>
                    <ShowChartIcon sx={{ fontSize: 15 }} />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Table editor">
                  <IconButton size="small" onClick={() => setCurveMode('table')}
                    sx={{ color: curveMode === 'table' ? C.orange : C.textMuted, p: 0.4 }}>
                    <TableRowsIcon sx={{ fontSize: 15 }} />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Reset curve to methodology">
                  <IconButton size="small" onClick={() => updateAndReseed({})} sx={{ color: C.textMuted, p: 0.4 }}>
                    <RestartAltIcon sx={{ fontSize: 15 }} />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>

            {curveMode === 'graph' ? (
              <DepreciationCurve
                curve={state.curve}
                referenceCurve={state.curveEdited ? seedCurve(state) : null}
                onChange={setCurve}
                c={C}
              />
            ) : (
              <Box>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr 1fr' }, gap: 0.5 }}>
                  {state.curve.map((p) => (
                    <Box key={p.age} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Typography sx={{ fontSize: '0.62rem', color: C.textMuted, width: 34, textAlign: 'right' }}>
                        Age {p.age}
                      </Typography>
                      <TextField
                        size="small"
                        type="number"
                        value={p.pct}
                        onChange={(e) => {
                          const pct = Math.max(0, Math.min(100, Number(e.target.value) || 0));
                          setCurve(state.curve.map((q) => (q.age === p.age ? { ...q, pct } : q)));
                        }}
                        InputProps={{ endAdornment: <Typography sx={{ fontSize: '0.6rem', color: C.textMuted }}>%</Typography> }}
                        sx={{ ...numFieldSx, flex: 1 }}
                      />
                    </Box>
                  ))}
                </Box>
                <Typography sx={{ fontSize: '0.6rem', color: C.textMuted, mt: 0.5 }}>
                  Remaining value % vs brand-new at each age — values in between are interpolated
                </Typography>
              </Box>
            )}

            {/* Methodology explanation */}
            {isLeasehold ? (
              <InfoBox c={C} accent={C.orange}>
                <b>Linear</b> writes value off in a straight line over the lease term: depreciation is
                age ÷ {state.linearTermYears} yrs (≈{(100 / state.linearTermYears).toFixed(2)}%/yr), reaching 100% —
                value $0 — at age {state.linearTermYears}.
              </InfoBox>
            ) : (
              state.freeholdMethod === 'landBuilding' ? (
                <InfoBox c={C} accent={C.orange}>
                  <b>Land + building split (cost approach).</b> Freehold land never expires, so no lease-style decay applies —
                  only the building depreciates. Value = land + building; the building share ({100 - state.landSharePct}%)
                  depreciates straight-line over its {state.buildingLifeYears}-year economic life
                  (≈{((100 - state.landSharePct) / state.buildingLifeYears).toFixed(1)}%/yr of total value).
                  E.g. a $2.0m property at 75% land: after 10 yrs only the $500k building loses 20% → total −5%.
                </InfoBox>
              ) : (
                <InfoBox c={C} accent={C.orange}>
                  <b>Market-derived age discount.</b> Rather than a theoretical schedule, use the discount actually observed
                  between new and old freehold comparables (e.g. 20-yr-old stock at $2,250 psf vs new at $2,500 psf → 10%,
                  ≈0.5%/yr). This captures ageing + obsolescence as the market prices it. URA controls for age in its own
                  index — market evidence beats a fixed rule.
                </InfoBox>
              )
            )}
              </>
            )}
            </Collapse>
          </Box>
        )}

        {/* ── Level 3 — physical & functional ── */}
        {state.level >= 3 && (
          <Box ref={(node: HTMLDivElement | null) => { levelRefs.current[3] = node; }} sx={{ mb: 1.5, p: 1.25, borderRadius: 1.5, border: `1px solid ${C.yellow}55`, bgcolor: `${C.yellow}08` }}>
            <Box
              sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer' }}
              onClick={() => { if (state.level > 3) setLevel3Open((v) => !v); }}
            >
              <Typography sx={{ fontSize: '0.7rem', fontWeight: 800, color: C.yellow }}>
                LEVEL 3 · PHYSICAL &amp; FUNCTIONAL
              </Typography>
              {state.level > 3 && (
                <IconButton size="small" sx={{ color: C.textMuted, p: 0.25 }}>
                  {level3Open ? <ExpandLessIcon sx={{ fontSize: 16 }} /> : <ExpandMoreIcon sx={{ fontSize: 16 }} />}
                </IconButton>
              )}
            </Box>

            <Collapse in={level3Open}>
              {/* Age bands */}
              <Typography sx={{ fontSize: '0.66rem', fontWeight: 700, color: C.text, mt: 0.75, mb: 0.5 }}>
                Age-band depreciation (added back to PSF)
              </Typography>
              {state.ageBands.map((b) => (
                  <Box key={b.id} sx={{ display: 'flex', gap: 0.5, alignItems: 'center', mb: 0.5, flexWrap: 'wrap' }}>
                  <DraftNumberField value={b.fromAge}
                    onChange={(value) => updateBand(b.id, { fromAge: value })}
                    sx={{ ...numFieldSx, width: 52 }} />
                  <Typography sx={{ fontSize: '0.62rem', color: C.textMuted }}>–</Typography>
                  <DraftNumberField value={b.toAge}
                    onChange={(value) => updateBand(b.id, { toAge: value })}
                    sx={{ ...numFieldSx, width: 52 }} />
                  <Typography sx={{ fontSize: '0.6rem', color: C.textMuted, mr: 0.25 }}>yrs</Typography>
                  <DraftNumberField value={b.value}
                    onChange={(value) => updateBand(b.id, { value })}
                    sx={{ ...numFieldSx, width: 60 }} />
                  <ToggleButtonGroup
                    value={b.mode} exclusive size="small"
                    onChange={(_, v) => { if (v) updateBand(b.id, { mode: v }); }}
                    sx={{
                      '& .MuiToggleButton-root': { py: 0.1, px: 0.6, fontSize: '0.6rem', color: C.textMuted, borderColor: C.border },
                      '& .Mui-selected': { bgcolor: `${C.yellow}22 !important`, color: `${C.yellow} !important` },
                    }}
                  >
                    <ToggleButton value="pct">%</ToggleButton>
                    <ToggleButton value="psf">$</ToggleButton>
                  </ToggleButtonGroup>
                  <IconButton
                    size="small"
                    onClick={() => onChange({ ...state, ageBands: state.ageBands.filter((x) => x.id !== b.id) })}
                    sx={{ color: C.textMuted, p: 0.25 }}
                  >
                    <DeleteOutlineIcon sx={{ fontSize: 14 }} />
                  </IconButton>
                </Box>
              ))}
              <Button
                size="small"
                startIcon={<AddIcon sx={{ fontSize: 13 }} />}
                onClick={() => {
                  const last = state.ageBands[state.ageBands.length - 1];
                  const from = last ? last.toAge : 0;
                  onChange({
                    ...state,
                    ageBands: [...state.ageBands, {
                      id: `band-${Date.now()}`, fromAge: from, toAge: from + 10, value: 0, mode: 'pct',
                    }],
                  });
                }}
                sx={{ color: C.yellow, textTransform: 'none', fontSize: '0.64rem', p: 0.25 }}
              >
                Add age band
              </Button>

              {/* Extras */}
              <Typography sx={{ fontSize: '0.66rem', fontWeight: 700, color: C.text, mt: 1, mb: 0.5 }}>
                Other adjustments
              </Typography>
              {state.extras.map((x) => (
                <Box key={x.id} sx={{ display: 'flex', gap: 0.5, alignItems: 'center', mb: 0.5, flexWrap: 'wrap' }}>
                  <Switch
                    size="small"
                    checked={x.enabled}
                    onChange={(e) => updateExtra(x.id, { enabled: e.target.checked })}
                    sx={{ '& .Mui-checked': { color: C.yellow }, '& .Mui-checked + .MuiSwitch-track': { bgcolor: `${C.yellow} !important` } }}
                  />
                  <TextField
                    size="small"
                    value={x.label}
                    onChange={(e) => updateExtra(x.id, { label: e.target.value })}
                    sx={{ ...numFieldSx, flex: 1, opacity: x.enabled ? 1 : 0.55 }}
                  />
                  <DraftNumberField value={x.value}
                    onChange={(value) => updateExtra(x.id, { value })}
                    sx={{ ...numFieldSx, width: 58, opacity: x.enabled ? 1 : 0.55 }} />
                  <ToggleButtonGroup
                    value={x.mode} exclusive size="small"
                    onChange={(_, v) => { if (v) updateExtra(x.id, { mode: v }); }}
                    sx={{
                      '& .MuiToggleButton-root': { py: 0.1, px: 0.6, fontSize: '0.6rem', color: C.textMuted, borderColor: C.border },
                      '& .Mui-selected': { bgcolor: `${C.yellow}22 !important`, color: `${C.yellow} !important` },
                    }}
                  >
                    <ToggleButton value="pct">%</ToggleButton>
                    <ToggleButton value="psf">$</ToggleButton>
                  </ToggleButtonGroup>
                  <IconButton
                    size="small"
                    onClick={() => onChange({ ...state, extras: state.extras.filter((e2) => e2.id !== x.id) })}
                    sx={{ color: C.textMuted, p: 0.25 }}
                  >
                    <DeleteOutlineIcon sx={{ fontSize: 14 }} />
                  </IconButton>
                </Box>
              ))}
              <Button
                size="small"
                startIcon={<AddIcon sx={{ fontSize: 13 }} />}
                onClick={() => onChange({
                  ...state,
                  extras: [...state.extras, {
                    id: `extra-${Date.now()}`, label: 'Custom adjustment', value: 0, mode: 'pct', enabled: true,
                  }],
                })}
                sx={{ color: C.yellow, textTransform: 'none', fontSize: '0.64rem', p: 0.25 }}
              >
                Add adjustment
              </Button>

              <InfoBox c={C} accent={C.yellow}>
                Level 3 adds back what an aged building loses beyond tenure: physical wear (finishes, M&amp;E systems)
                via the age bands, plus optional functional/market items — the premium buyers pay for a brand-new
                launch, renovation condition, and outdated facilities or layouts. Percentages apply to the raw avg PSF;
                $ values add directly. Stacked on top of Level 2.
              </InfoBox>
            </Collapse>
          </Box>
        )}

        {state.level === 4 && (
          <Box ref={(node: HTMLDivElement | null) => { levelRefs.current[4] = node; }} sx={{ mb: 1.5, p: 1.25, borderRadius: 1.5, border: `1px solid ${C.orange}55`, bgcolor: `${C.orange}08` }}>
            <Typography sx={{ fontSize: '0.7rem', fontWeight: 800, color: C.orange, mb: 0.75 }}>
              LEVEL 4 · AMENITY PREMIUM
            </Typography>
            <Typography sx={{ fontSize: '0.64rem', color: C.textMuted, lineHeight: 1.45 }}>
              Set how close your buyer wants the MRT and a primary school. Projects farther away are a weaker
              location — the premium you enter lifts their PSF to what a well-located home would cost.
            </Typography>
            <Box sx={{
              display: 'flex', alignItems: 'center', gap: 0.75, mt: 1, p: 0.75, borderRadius: 1.5,
              bgcolor: amenityTotals.lifted ? `${C.yellow}1a` : C.surface,
              border: `1px solid ${amenityTotals.lifted ? `${C.yellow}66` : C.border}`,
              transition: 'all 200ms',
            }}>
              <EmojiEventsIcon sx={{ fontSize: 22, color: amenityTotals.lifted ? C.yellow : C.textMuted }} />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontSize: '0.7rem', fontWeight: 800, color: C.text }}>
                  {amenityTotals.lifted
                    ? `${amenityTotals.lifted.toLocaleString()} project${amenityTotals.lifted === 1 ? '' : 's'} lifted`
                    : 'No premium in play yet'}
                </Typography>
                <Typography sx={{ fontSize: '0.6rem', color: C.textMuted }}>
                  {amenityTotals.lifted ? 'Average amenity premium per lifted project' : 'Pick a distance, then a premium below'}
                </Typography>
              </Box>
              <Typography sx={{ fontSize: '1rem', fontWeight: 900, color: amenityTotals.lifted ? C.yellow : C.textMuted }}>
                +${amenityTotals.avg.toLocaleString()}
              </Typography>
            </Box>
            <AmenityPremiumCard
              title="MRT Access"
              icon={<TrainIcon />}
              accent={C.orange}
              premium={state.mrtPremium}
              onChange={(mrtPremium) => onChange({ ...state, mrtPremium })}
              points={amenityPoints.mrt}
              distancePresets={[300, 500, 800, 1000]}
              c={C}
            />
            <AmenityPremiumCard
              title="Primary School"
              icon={<SchoolIcon />}
              accent={C.yellow}
              premium={state.priSchPremium}
              onChange={(priSchPremium) => onChange({ ...state, priSchPremium })}
              points={amenityPoints.priSch}
              distancePresets={[500, 1000, 2000]}
              c={C}
            />
          </Box>
        )}
      </Box>
    </Box>
  );
}
